import { useState } from 'react';
import ScrollReveal from './ScrollReveal';

type SubModule = {
  globalNum: number;
  title: string;
};

type ModuleData = {
  num: string;
  title: string;
  submodules: SubModule[];
};

const modules: ModuleData[] = [
  {
    num: '01',
    title: 'Introducción',
    submodules: [
      { globalNum: 1, title: 'Bienvenida' },
      { globalNum: 2, title: 'Herramientas Necesarias' },
    ],
  },
  {
    num: '02',
    title: 'Herramientas de Trabajo',
    submodules: [
      { globalNum: 3, title: 'Crear Fan Page y conectar con Instagram de Negocios' },
      { globalNum: 4, title: 'Instagram para Negocios' },
      { globalNum: 5, title: 'WhatsApp Business' },
      { globalNum: 6, title: 'Vinculación de WhatsApp Business' },
      { globalNum: 7, title: 'Jerarquía de Meta Ads' },
      { globalNum: 8, title: 'Creación del Business Manager' },
      { globalNum: 9, title: 'Creación de la Cuenta Publicitaria' },
    ],
  },
  {
    num: '03',
    title: 'Campañas Publicitarias',
    submodules: [
      { globalNum: 10, title: 'Tipos de Campaña' },
      { globalNum: 11, title: 'Públicos Personalizados y Similares' },
      { globalNum: 12, title: 'Ciclo de Venta' },
      { globalNum: 13, title: 'Campaña de Posicionamiento' },
      { globalNum: 14, title: 'Campaña de Mensajes a WhatsApp' },
      { globalNum: 15, title: 'Remarketing' },
    ],
  },
  {
    num: '04',
    title: 'Investigación del Cliente Ideal',
    submodules: [
      { globalNum: 16, title: 'Tipos de Segmentación' },
      { globalNum: 17, title: 'Prompt de ChatGPT para Encontrar el Cliente Ideal' },
    ],
  },
  {
    num: '05',
    title: 'Cierre de Ventas',
    submodules: [
      { globalNum: 18, title: 'Cierre de Ventas: Técnica PP' },
      { globalNum: 19, title: 'Remarketing Orgánico' },
    ],
  },
];

export default function CourseContent() {
  const [expandedModules, setExpandedModules] = useState<Record<string, boolean>>({
    '01': true,
  });

  const toggleModule = (num: string) => {
    setExpandedModules((prev) => ({
      ...prev,
      [num]: !prev[num],
    }));
  };

  return (
    <section id="course-content" className="py-20 md:py-28 bg-[#FFFFFF]">
      <div className="mx-auto max-w-[1280px] px-6 md:px-10">
        <ScrollReveal>
          <div className="text-center mb-14">
            <span className="inline-block text-xs font-semibold tracking-wider uppercase text-[#C8A24A] mb-3">
              Programa
            </span>
            <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-[#111111]">
              Contenido del Taller
            </h2>
          </div>
        </ScrollReveal>

        <div className="relative max-w-[800px] mx-auto">
          <div className="absolute left-8 top-0 bottom-0 w-px bg-gradient-to-b from-[#E53935]/30 via-[#E53935]/15 to-transparent hidden md:block"></div>

          <div className="space-y-0">
            {modules.map((mod, idx) => {
              const isExpanded = expandedModules[mod.num] ?? false;

              return (
                <ScrollReveal key={mod.num} delay={idx * 60}>
                  <div className="relative flex flex-col md:flex-row items-start gap-4 md:gap-8 pb-10 md:pb-12 last:pb-0">
                    {/* Number circle */}
                    <div className="flex-shrink-0 w-16 h-16 rounded-2xl bg-white border border-gray-100 flex items-center justify-center shadow-[0_4px_16px_rgba(0,0,0,0.03)] z-10 relative">
                      <span className="font-heading text-lg font-bold text-[#E53935]">{mod.num}</span>
                    </div>

                    {/* Content */}
                    <div className="flex-1 pt-0 md:pt-0">
                      <button
                        type="button"
                        role="button"
                        tabIndex={0}
                        onClick={() => toggleModule(mod.num)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            toggleModule(mod.num);
                          }
                        }}
                        className="w-full bg-[#F7F8FA] rounded-2xl p-5 md:p-6 border border-gray-50 hover:border-[#E53935]/10 transition-all duration-300 hover:shadow-[0_8px_30px_rgba(0,0,0,0.04)] cursor-pointer text-left group"
                      >
                        <div className="flex items-center justify-between gap-3">
                          <h3 className="font-heading text-lg md:text-xl font-semibold text-[#111111]">
                            Módulo {mod.num}. {mod.title}
                          </h3>
                          <div className="flex-shrink-0 w-8 h-8 rounded-full bg-white border border-gray-100 flex items-center justify-center transition-transform duration-300 group-hover:border-[#E53935]/20">
                            <i
                              className={`ri-arrow-down-s-line text-[#888888] text-sm transition-transform duration-300 ${
                                isExpanded ? 'rotate-180' : 'rotate-0'
                              }`}
                            ></i>
                          </div>
                        </div>
                      </button>

                      {/* Submodules */}
                      <div
                        className={`overflow-hidden transition-all duration-400 ease-in-out ${
                          isExpanded ? 'max-h-[2000px] opacity-100 mt-3' : 'max-h-0 opacity-0'
                        }`}
                      >
                        <div className="space-y-1 pl-0 md:pl-2">
                          {mod.submodules.map((sub) => (
                            <div
                              key={sub.globalNum}
                              className="flex items-center gap-3 py-2.5 px-4 rounded-xl hover:bg-[#F7F8FA] transition-colors duration-200 group/sub"
                            >
                              <span className="flex-shrink-0 w-7 h-7 rounded-lg bg-[#E53935]/8 flex items-center justify-center">
                                <span className="text-xs font-bold text-[#E53935]">{sub.globalNum}</span>
                              </span>
                              <span className="text-sm text-[#555555] group-hover/sub:text-[#111111] transition-colors duration-200 leading-snug">
                                {sub.title}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                </ScrollReveal>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}